import React from 'react';
import { 
  Calendar, 
  MapPin, 
  Clock, 
  FileText, 
  AlertCircle, 
  X, 
  CalendarClock,
  RefreshCw,
  UserCircle
} from 'lucide-react';
import { formatDate, formatTime } from '@/lib/date-utils';
import { Button } from '@/components/ui/button';
import { AppNordModal } from '@/components/ui/AppNordModal';
import { Appointment } from '@/types/appointment-types';
import { Badge } from '@/components/ui/badge';
import { addToCalendar, getMapUrl } from '@/lib/appointment-utils';

interface AppointmentDetailViewProps {
  appointment: Appointment | null;
  isOpen: boolean;
  onClose: () => void;
  onReschedule: (appointment: Appointment) => void;
  onCancel: (appointmentId: string) => void;
}

const AppointmentDetailView: React.FC<AppointmentDetailViewProps> = ({
  appointment,
  isOpen,
  onClose,
  onReschedule,
  onCancel
}) => {
  if (!appointment) return null;

  // Determine if the appointment can be rescheduled or cancelled
  const canReschedule = ['scheduled', 'confirmed'].includes(appointment.status);
  const canCancel = ['scheduled', 'confirmed'].includes(appointment.status);
  
  // Convert start and end times to Date objects
  const startDate = new Date(appointment.startTime);
  const endDate = new Date(appointment.endTime);
  
  // Format appointment date and time
  const formattedDate = formatDate(startDate);
  const formattedStartTime = formatTime(startDate);
  const formattedEndTime = formatTime(endDate);
  const formattedTimeRange = `${formattedStartTime} - ${formattedEndTime}`;
  
  // Prepare the appointment location
  const location = appointment.facilityId || 'Main Clinic'; // Fallback
  
  // Prepare provider name
  const displayProviderName = appointment.providerName 
    ? appointment.providerName  // providerName already includes "Dr." prefix from the API
    : 'Provider information not available';
  
  // Status badge style
  const getStatusBadge = () => {
    let className = '';
    
    switch (appointment.status) {
      case 'scheduled':
        className = 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
        break;
      case 'confirmed':
        className = 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
        break;
      case 'completed':
        className = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
        break;
      case 'cancelled':
        className = 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
        break;
      case 'rescheduled':
        className = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
        break;
      default:
        className = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
    
    return (
      <Badge className={className}>
        {appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}
      </Badge>
    );
  };

  // Handler for adding to calendar
  const handleAddToCalendar = () => {
    addToCalendar({
      title: appointment.appointmentType || 'Medical Appointment',
      description: appointment.reason || 'No description provided',
      startTime: appointment.startTime,
      endTime: appointment.endTime,
      location
    });
  };

  // Handler for getting directions
  const handleGetDirections = () => {
    const url = getMapUrl(location);
    window.open(url, '_blank');
  };

  return (
    <AppNordModal
      open={isOpen}
      onClose={onClose}
      heading={`${appointment.appointmentType || 'Appointment'} Details`}
      size="m"
    >
      <div className="py-4 space-y-6">
        {/* Status */}
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium">Status</h3>
          {getStatusBadge()}
        </div>
        
        {/* Provider */}
        <div className="space-y-1">
          <div className="flex items-center text-muted-foreground mb-1">
            <UserCircle className="h-4 w-4 mr-2" />
            <span>Provider</span>
          </div>
          <p className="text-lg">{displayProviderName}</p>
        </div>
        
        {/* Date & Time */}
        <div className="space-y-1">
          <div className="flex items-center text-muted-foreground mb-1">
            <Calendar className="h-4 w-4 mr-2" />
            <span>Date & Time</span>
          </div>
          <p className="text-lg">{formattedDate}</p>
          <div className="flex items-center">
            <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
            <span>{formattedTimeRange}</span>
          </div>
        </div>
        
        {/* Location */}
        <div className="space-y-1">
          <div className="flex items-center text-muted-foreground mb-1">
            <MapPin className="h-4 w-4 mr-2" />
            <span>Location</span>
          </div>
          <p className="text-lg">{location}</p>
          <Button 
            variant="outline" 
            size="sm" 
            className="mt-2"
            onClick={handleGetDirections}
          >
            <MapPin className="h-4 w-4 mr-2" />
            Get Directions
          </Button>
        </div>
        
        {/* Reason */}
        {appointment.reason && (
          <div className="space-y-1">
            <div className="flex items-center text-muted-foreground mb-1">
              <FileText className="h-4 w-4 mr-2" />
              <span>Reason for Visit</span>
            </div>
            <p>{appointment.reason}</p>
          </div>
        )}
        
        {/* Preparation Instructions */}
        <div className="space-y-1 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-md">
          <div className="flex items-center text-blue-700 dark:text-blue-300 font-medium mb-1">
            <AlertCircle className="h-4 w-4 mr-2" />
            <span>Preparation Instructions</span>
          </div>
          <p className="text-blue-600 dark:text-blue-300 text-sm">
            {appointment.notes || 
             `Please arrive 15 minutes before your scheduled time. Bring your insurance card and photo ID. 
             If this is a lab appointment, follow any fasting instructions provided by your doctor.`}
          </p>
        </div>
        
        {/* Notes */}
        {appointment.notes && (
          <div className="space-y-1">
            <div className="flex items-center text-muted-foreground mb-1">
              <FileText className="h-4 w-4 mr-2" />
              <span>Additional Notes</span>
            </div>
            <p className="text-sm">{appointment.notes}</p>
          </div>
        )}
        
        {/* Add to Calendar Button */}
        <Button 
          variant="outline" 
          className="w-full"
          onClick={handleAddToCalendar}
        >
          <CalendarClock className="h-4 w-4 mr-2" />
          Add to Calendar
        </Button>
      </div>
      
      <div className="flex flex-col sm:flex-row gap-2 mt-6">
        {canReschedule && (
          <Button 
            variant="outline"
            onClick={() => onReschedule(appointment)}
            className="flex-1"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Reschedule
          </Button>
        )}
        
        {canCancel && (
          <Button 
            variant="destructive"
            onClick={() => onCancel(appointment.id)}
            className="flex-1"
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
        )}
        
        <Button 
          variant="secondary"
          onClick={onClose}
          className="flex-1"
        >
          Close
        </Button>
      </div>
    </AppNordModal>
  );
};

export default AppointmentDetailView; 